import React, { useState, memo } from 'react';
// GalleryHorizontal (a pager: one panel between two others) rather than a plain
// square, which read as an unchecked checkbox; Rows3 (stacked rows) for the
// whole form listed on one page.
import { ChevronDown, Rows3, GalleryHorizontal } from 'lucide-react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { DawidConfirm } from './confirm.jsx';
import { cn } from '../lib/utils';

// Switches between answering one question at a time and a continuous page of all
// questions. Purely a display preference — both drive the same flow engine.
function ViewModeToggle({ continuousMode, setContinuousMode }) {
  return (
    // .tabs / .tab / .tab-active — the design system's segmented control.
    <div className="tabs" role="group" aria-label="Question layout">
      <button
        type="button"
        onClick={() => setContinuousMode(false)}
        aria-pressed={!continuousMode}
        title="Show one question at a time"
        className={cn(
          'flex items-center gap-1.5 text-xs',
          !continuousMode ? 'tab-active' : 'tab'
        )}
      >
        <GalleryHorizontal className="w-3.5 h-3.5" />
        One at a time
      </button>
      <button
        type="button"
        onClick={() => setContinuousMode(true)}
        aria-pressed={!!continuousMode}
        title="Show all questions on one page"
        className={cn(
          'flex items-center gap-1.5 text-xs',
          continuousMode ? 'tab-active' : 'tab'
        )}
      >
        <Rows3 className="w-3.5 h-3.5" />
        Continuous
      </button>
    </div>
  );
}

function DawidMenu({ menuFixed, currentFlowId, flows, changeFlow, goSection, continuousMode, setContinuousMode }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(currentFlowId);
  const [activeItem, setActiveItem] = useState('data_collection');

  const setActive = (item) => {
    setActiveItem(item);
    goSection(item);
  };
  const selectFlow = (flowId) => {
    setSelectedFlow(flowId);
    setConfirmOpen(true);
  };

  let label;
  if (currentFlowId !== undefined) {
    const currentFlow = flows[currentFlowId];
    label = currentFlow.name + ' (v' + currentFlow.version + ')';
  } else {
    label = 'Form flows';
  }

  return (
    <>
      <nav
        className={cn(
          'no-print w-full bg-white mb-4 transition-shadow duration-500',
          menuFixed && 'sticky top-0 z-40 shadow-md'
        )}
      >
        {/* .tabs-underline / .tab-underline — selected state lives in
            aria-selected, which is where the design system reads it from. */}
        <div className="tabs-underline items-center gap-6 pt-2" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeItem === 'data_collection'}
            onClick={() => setActive('data_collection')}
            className="tab-underline"
          >
            Data collection
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeItem === 'summary'}
            onClick={() => setActive('summary')}
            className="tab-underline"
          >
            Summary
          </button>

          <div className="ml-auto flex items-center gap-2 pb-2">
            <ViewModeToggle continuousMode={continuousMode} setContinuousMode={setContinuousMode} />
            <DropdownMenuPrimitive.Root>
              <DropdownMenuPrimitive.Trigger asChild>
                <button
                  type="button"
                  className="nav-link flex items-center gap-1.5"
                >
                  {label}
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </button>
              </DropdownMenuPrimitive.Trigger>
              <DropdownMenuPrimitive.Portal>
                <DropdownMenuPrimitive.Content
                  align="end"
                  sideOffset={4}
                  className="z-50 min-w-[200px] rounded-xl border border-gray-200 bg-white shadow-dropdown p-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                >
                  {Object.keys(flows).map((flowId, keyIndex) => {
                    const flow = flows[flowId];
                    return (
                      <DropdownMenuPrimitive.Item
                        key={'dflow' + keyIndex}
                        onSelect={() => selectFlow(flowId)}
                        // Selected/hover follow the design system's facet rows:
                        // sky tint for the current choice, gray for hover.
                        className={cn(
                          'relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none transition-colors',
                          flowId === currentFlowId
                            ? 'bg-info-50 font-medium text-primary-900 focus:bg-info-100'
                            : 'text-primary-900 focus:bg-gray-50'
                        )}
                      >
                        {flow.name} (v{flow.version})
                      </DropdownMenuPrimitive.Item>
                    );
                  })}
                </DropdownMenuPrimitive.Content>
              </DropdownMenuPrimitive.Portal>
            </DropdownMenuPrimitive.Root>
          </div>
        </div>
      </nav>

      <DawidConfirm
        open={confirmOpen}
        onConfirm={() => {
          changeFlow(selectedFlow);
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
        content="Are you sure you want to switch to a different flow? All progress that has not been saved will be lost."
        cancelText="I changed my mind, don't switch"
        confirmText="Change flow"
      />
    </>
  );
}

export default memo(DawidMenu, (prev, next) => (
  prev.menuFixed === next.menuFixed &&
  prev.currentFlowId === next.currentFlowId &&
  prev.flows === next.flows &&
  prev.continuousMode === next.continuousMode
));
